/**
 * What being the manager actually gets you, said once.
 *
 * The same list has to appear in the create wizard and would drift the moment a
 * power was added to the league page and only one of the two descriptions got
 * updated - the `gameKinds.js` problem, same fix.
 *
 * Every entry is a pair, and the pair is the whole point. `name` is the extra
 * button; `fallback` is what happens if the manager never presses it. A list of
 * powers on its own reads as a list of chores - somebody volunteering to set up a
 * game for their mates does not want a job description - so no power is ever
 * stated without the default beside it. Nothing here is a thing that must be done.
 *
 * Two rules for this copy:
 *  - never state a power whose default is "the thing stops working". If a manager
 *    ignoring it breaks the game, it is not an extra capability, it is a chore,
 *    and it belongs on the game page where it can be prompted for - not here.
 *  - keep `fallback` in the present tense and about the game, not about the
 *    manager. "The roster settles itself" reassures; "you don't have to do
 *    anything" is the same fact worded as an obligation being waived.
 */

/** Every power's icon name, resolved to a lucide component by the step. */
export const MANAGER_POWERS = {
  standalone: {
    intro:
      'You run this one. That is less than it sounds \u2014 the game plays itself from here: '
      + 'deadlines, results, who is out. Below is what you get on top, and every one of them '
      + 'already has an answer if you never touch it.',
    powers: [
      {
        id: 'invite',
        icon: 'invite',
        name: 'Invite people yourself',
        fallback: 'your game code does the rounds in the group chat and people join themselves.',
      },
      {
        id: 'roster',
        icon: 'remove',
        name: 'Take somebody back off the roster',
        fallback: 'the line-up is whoever turned up, and it settles the moment the first results land.',
      },
      {
        id: 'gaffer',
        icon: 'gaffer',
        name: 'Change your mind about the Gaffer',
        fallback: 'he is in or out exactly as you left him a moment ago.',
      },
      {
        id: 'league',
        icon: 'league',
        name: 'Grow it into a Mini League later',
        fallback: 'it stays a one-off \u2014 one game, one winner, and that is that.',
      },
    ],
  },

  league: {
    intro:
      'You run this one. That is less than it sounds \u2014 when a game finishes the next one starts '
      + 'on its own and the table keeps itself. Below is what you get on top, and every one of '
      + 'them already has an answer if you never touch it.',
    powers: [
      {
        id: 'tournaments',
        icon: 'league',
        name: 'Add another competition whenever you fancy',
        fallback: 'the one you have just picked runs its full season, a game at a time.',
      },
      {
        id: 'requests',
        icon: 'approve',
        name: 'Say yes to latecomers',
        fallback: 'nothing to approve until the first game kicks off \u2014 before that the code lets everyone straight in.',
      },
      {
        id: 'members',
        icon: 'remove',
        name: 'Take somebody off the list',
        fallback: 'nobody goes anywhere unless they decide to leave of their own accord.',
      },
      {
        id: 'gaffer',
        icon: 'gaffer',
        name: 'Decide on the Gaffer for the season',
        fallback: 'he is in or out of every game the league starts, exactly as you left him.',
      },
      {
        id: 'succession',
        icon: 'crown',
        name: 'Hand the job on, or call the whole thing off',
        fallback: 'neither is anything you need to think about today.',
      },
    ],
  },
};

/**
 * The line that closes the step, shared because it is the same fact in both.
 *
 * It is here rather than in either list because it is the one thing on the screen
 * that is not a power: it says the manager plays under exactly the same rules as
 * everybody else. Somebody worrying that they have taken on responsibility for
 * other people's football is worrying about the wrong thing, and this is the
 * sentence that says so.
 */
export const MANAGER_POWERS_OUTRO =
  'And you play it like everyone else. Forget to pick and the game picks for you, same as '
  + 'the rest of them \u2014 running it does not make you responsible for anybody\u2019s football.';

/**
 * The Gaffer, on managers, once the thing is actually created.
 *
 * One line, in his voice, and it is thanks rather than instruction - by this point
 * the setup is done and there is nothing left to tell anybody. It is shown whether
 * or not he was let into the game: he is the voice of the place, not a feature that
 * was switched on.
 *
 * Same three rules as everywhere else he speaks - he is not a bot, he never explains
 * how he picks, and one line is the whole of it.
 */
export const GAFFER_MANAGER_THANKS =
  'Right \u2014 someone has to actually set the thing up, and it is never the loudest one in '
  + 'the group chat. Managers are what makes this whole daft dream work. Legends, the lot of '
  + 'you. Now let us see if you are any good at it.';
